import { defineStore } from 'pinia';
import { markRaw } from 'vue';

const createId = () => `tab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const normalizeId = (value) => {
  if (value === null || value === undefined) return value;
  return String(value);
};

const toRawComponent = (value) => (value ? markRaw(value) : value);
const toRawIcon = (value) => (value ? markRaw(value) : value);

export const useTabsStore = defineStore('tabs', {
  getters: {
    canGoBack: (state) => state.tabVisitStack.length > 1,
  },
  state: () => ({
    tabs: [],
    activeTabId: null,
    tabVisitStack: [],
  }),
  actions: {
    recordTabVisit(id) {
      if (!id) return;
      const lastVisited = this.tabVisitStack[this.tabVisitStack.length - 1];
      if (lastVisited === id) return;
      this.tabVisitStack.push(id);
      if (this.tabVisitStack.length > 200) {
        this.tabVisitStack.shift();
      }
    },

    createTab({
      component,
      title,
      props = {},
      icon = null,
      itemId = null,
      section = null,
      listeners = {},
      tabKey = null,
    }) {
      if (!component) return null;
      const id = createId();
      const tab = {
        id,
        component: toRawComponent(component),
        title: title || 'Untitled',
        props,
        icon: toRawIcon(icon),
        itemId: normalizeId(itemId),
        section,
        listeners,
        tabKey,
      };
      this.tabs.push(tab);
      this.activateTab(id);
      return tab;
    },

    activateTab(id) {
      if (!id) return;
      const exists = this.tabs.some((tab) => tab.id === id);
      if (exists) {
        this.activeTabId = id;
        this.recordTabVisit(id);
      }
    },

    goBackTab() {
      if (this.tabVisitStack.length < 2) return null;
      this.tabVisitStack.pop();
      while (this.tabVisitStack.length) {
        const previousTabId = this.tabVisitStack.pop();
        const exists = this.tabs.some((tab) => tab.id === previousTabId);
        if (!exists) continue;
        this.activeTabId = previousTabId;
        this.tabVisitStack.push(previousTabId);
        return previousTabId;
      }
      if (this.activeTabId) {
        this.tabVisitStack = [this.activeTabId];
      } else {
        this.tabVisitStack = [];
      }
      return null;
    },

    closeTab(id) {
      const index = this.tabs.findIndex((tab) => tab.id === id);
      if (index === -1) return;

      const closingActive = this.activeTabId === id;
      this.tabs.splice(index, 1);
      this.tabVisitStack = this.tabVisitStack.filter((tabId) => tabId !== id);

      if (!this.tabs.length) {
        this.activeTabId = null;
        this.tabVisitStack = [];
        return;
      }

      if (closingActive) {
        const nextIndex = index >= this.tabs.length ? this.tabs.length - 1 : index;
        this.activateTab(this.tabs[nextIndex].id);
        return;
      }

      if (!this.tabs.some((tab) => tab.id === this.activeTabId)) {
        this.activateTab(this.tabs[0].id);
      }
    },

    closeAllTabs() {
      this.tabs = [];
      this.activeTabId = null;
      this.tabVisitStack = [];
    },

    resetTabs() {
      this.closeAllTabs();
    },

    updateTab(id, updates = {}) {
      if (!id) return;
      const tab = this.tabs.find((entry) => entry.id === id);
      if (!tab) return;

      if (updates.props) {
        tab.props = { ...(tab.props || {}), ...updates.props };
      }

      Object.entries(updates).forEach(([key, value]) => {
        if (key === 'props') return;
        if (key === 'component') {
          tab.component = toRawComponent(value);
          return;
        }
        if (key === 'icon') {
          tab.icon = toRawIcon(value);
          return;
        }
        tab[key] = value;
      });
    },

    updateActiveTabContent(updates = {}) {
      if (!this.activeTabId) return;
      this.updateTab(this.activeTabId, updates);
    },

    findMatchingTab({ component, itemId = undefined, section = null, tabKey = null }) {
      const normalizedItemId = normalizeId(itemId);
      return this.tabs.find((tab) => {
        if (section && tab.section !== section) return false;
        if (tabKey) {
          return tab.tabKey === tabKey;
        }
        const matchesComponent = tab.component === component;
        const matchesKey = !tab.tabKey;
        if (!matchesComponent || !matchesKey) return false;
        if (itemId === undefined) return true;
        return tab.itemId === normalizedItemId;
      });
    },
  },
});
